import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTokens } from '../context/TokenContext';
import { useXP } from '../context/XPContext';
import { discoverHintApi } from '../api/hints';
import apiClient from '../api/client';
import { HINTS, HINT_IDS, HINT_MAP, NODE_TYPE_TO_HINT, CONTENT_HINT_IDS } from './hints.config';

const HintCtx = createContext(null);

// Map intake-picked interest → the hint that highlights the picked
// DLC's areas inside the dashboard map after auto-DLC-mode load.
// Selectors live in hints.config.js; their `selector` and `page`
// must match what the dashboard actually renders post-intake.
export const POST_INTAKE_DLC_HINT_BY_INTEREST = {
  naplex: 'naplex_area_pick',
  ptcb: 'ptcb_area_pick',
  mpje: 'mpje_area_pick',
};

export function buildPostIntakeHintQueue(interests = []) {
  const queue = [];
  const firstInterest = Array.isArray(interests) ? interests.find(Boolean) : null;
  const dlcHintId = firstInterest ? POST_INTAKE_DLC_HINT_BY_INTEREST[firstInterest] : null;
  if (dlcHintId) {
    queue.push(dlcHintId);
  }
  queue.push('upload');
  return queue;
}

// Peek at the head of the post-intake queue, advancing past any
// already-seen / no-longer-applicable items WITHOUT consuming the
// active head. The head stays in the queue until the user clicks
// the highlighted element and `seenHints[head]` flips to true.
export function getNextPostIntakeHint(queue = [], seenHints = {}, interests = []) {
  const source = Array.isArray(queue) ? queue : [];
  const normalizedInterests = Array.isArray(interests) ? interests : [];

  for (let i = 0; i < source.length; i++) {
    const id = source[i];
    const hint = HINT_MAP[id];
    if (!hint) continue;
    if (seenHints?.[id]) continue;
    if (hint.requiresInterest && !normalizedInterests.includes(hint.requiresInterest)) continue;
    return {
      nextHintId: id,
      remainingQueue: source.slice(i),
    };
  }

  return {
    nextHintId: null,
    remainingQueue: [],
  };
}

export function HintProvider({ children }) {
  const { user, checkAuth } = useAuth();
  const userId = user?._id || user?.id;
  const { bumpDisplay: bumpTokens, refetchBalance: refetchTokens } = useTokens();
  const { refetchXP } = useXP();

  const storageKey = userId ? `debono_hints_seen_${userId}` : null;

  const [seenHints, setSeenHints] = useState({});
  const [hintsActive, setHintsActive] = useState(false);
  const [activeDiscoveryId, setActiveDiscoveryId] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showIntake, setShowIntake] = useState(false);
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [intakeExamInterests, setIntakeExamInterests] = useState([]);
  const [postIntakeQueue, setPostIntakeQueue] = useState([]);
  // Runtime gate: true once the user has at least one fully-parsed deck.
  // Hints with `requiresParsedDeck: true` (e.g. add_exam) are filtered
  // out by HintLayer until this flips. Updated by Dashboard via
  // setHasParsedDeck on every decks-list refresh.
  const [hasParsedDeck, setHasParsedDeck] = useState(false);

  // Re-initialize when userId changes (login/logout/switch)
  useEffect(() => {
    if (!userId) {
      setSeenHints({});
      setHintsActive(false);
      setActiveDiscoveryId(null);
      setShowWelcome(false);
      setShowIntake(false);
      setIntakeLoading(false);
      setIntakeExamInterests([]);
      setPostIntakeQueue([]);
      return;
    }
    const key = `debono_hints_seen_${userId}`;
    const welcomeKey = `debono_welcomed_${userId}`;
    const tutorialKey = `debono_tutorial_progress_${userId}`;
    const interestsKey = `debono_intake_interests_${userId}`;
    try {
      const cached = localStorage.getItem(interestsKey);
      setIntakeExamInterests(cached ? JSON.parse(cached) : []);
    } catch {
      setIntakeExamInterests([]);
    }
    setPostIntakeQueue([]);
    try {
      setSeenHints(JSON.parse(localStorage.getItem(key) || '{}'));
    } catch {
      setSeenHints({});
    }
    const welcomed = localStorage.getItem(welcomeKey);
    const tutorialProgress = localStorage.getItem(tutorialKey);
    // Admins don't see the onboarding welcome — they've built the app.
    // Auto-stamp the welcomed flag so the modal stops firing on every session.
    if (user?.role === 'admin' && !welcomed) {
      localStorage.setItem(welcomeKey, '1');
      setShowWelcome(false);
      setHintsActive(true);
    } else {
      setShowWelcome(!welcomed && !tutorialProgress);
      setHintsActive(Boolean(welcomed || tutorialProgress));
    }

    // Sync server-side discoveries into localStorage so hints don't re-glow
    // after browser/device changes or localStorage clears.
    // Merge (not replace) to avoid overwriting optimistic discoveries from confirmDiscovery().
    const controller = new AbortController();
    setIntakeLoading(true);
    setShowIntake(false);

    apiClient.get('/api/auth/intake', { signal: controller.signal }).then((res) => {
      const completed = Boolean(res.data?.completed);
      setShowIntake(!completed);
      // Stale sessionStorage from a prior session can leave naplex_mode='1'
      // around, which makes the Dashboard auto-show the NAPLEX map BEHIND
      // the IntakeModal — pre-intake the dashboard should be empty. Clear
      // the DLC-mode + force-node-view flags whenever intake is required.
      if (!completed) {
        try {
          ['naplex', 'ptcb', 'mpje'].forEach((dlc) => {
            sessionStorage.removeItem(`${dlc}_mode`);
          });
        } catch {}
      }
    }).catch(() => {
      setShowIntake(false);
    }).finally(() => {
      setIntakeLoading(false);
    });

    apiClient.get('/api/hints/discovered', { signal: controller.signal }).then(res => {
      const serverDiscovered = res.data?.discovered || [];
      if (serverDiscovered.length > 0) {
        setSeenHints(prev => {
          const merged = { ...prev };
          for (const hintId of serverDiscovered) merged[hintId] = true;
          if (key) localStorage.setItem(key, JSON.stringify(merged));
          return merged;
        });
        // Server has proof this user has been through onboarding — suppress
        // the welcome modal on this browser and activate hints, so opening
        // the app from a fresh browser profile doesn't re-show intro.
        if (!localStorage.getItem(welcomeKey)) {
          localStorage.setItem(welcomeKey, '1');
          setShowWelcome(false);
          setHintsActive(true);
        }
      }
    }).catch(() => {}); // non-fatal
    return () => controller.abort();
  }, [userId, user?.role]);

  // Gate-aware pending check
  const isHintPending = useCallback((hintId) => {
    if (!hintsActive) return false;
    if (seenHints[hintId]) return false;
    const hint = HINT_MAP[hintId];
    if (!hint) return false;
    if (hint.gatedBy?.length > 0) {
      for (const gateId of hint.gatedBy) {
        if (!seenHints[gateId]) return false;
      }
    }
    return true;
  }, [hintsActive, seenHints]);

  // Open discovery modal
  const triggerDiscovery = useCallback((hintId) => {
    setActiveDiscoveryId(hintId);
  }, []);

  // Close modal without claiming
  const dismissDiscovery = useCallback(() => {
    setActiveDiscoveryId(null);
  }, []);

  // Refs for stable callback access in confirmDiscovery
  const bumpRef = useRef({ bumpTokens, refetchTokens, refetchXP });
  useEffect(() => {
    bumpRef.current = { bumpTokens, refetchTokens, refetchXP };
  });

  // Claim reward + mark discovered
  const confirmDiscovery = useCallback(async () => {
    if (!activeDiscoveryId) return;
    const hintId = activeDiscoveryId;
    const hint = HINT_MAP[hintId];

    // 1. Mark discovered in localStorage
    setSeenHints(prev => {
      const next = { ...prev, [hintId]: true };
      if (storageKey) localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });

    // 2. Close modal
    setActiveDiscoveryId(null);

    // 3. Optimistic token bump
    const { bumpTokens: bump, refetchTokens: rTokens, refetchXP: rXP } = bumpRef.current;
    if (hint?.tokens && bump) bump(hint.tokens);

    // 4. Backend API
    try {
      const result = await discoverHintApi(hintId);
      if (rTokens) rTokens();
      if (rXP) rXP();

      // 5. Demo cleanup when all content hints discovered
      if (result.all_content_discovered) {
        try {
          await apiClient.post('/api/hints/cleanup-demo');
          window.dispatchEvent(new CustomEvent('hints:content-complete'));
        } catch { /* non-fatal */ }
      }
    } catch (err) {
      console.error('Hint discovery API failed:', err);
    }
  }, [activeDiscoveryId, storageKey]);

  // Mark a hint seen WITHOUT routing through the modal flow. Used for
  // "click the glowing thing, no modal" hints (e.g. area-pick post-intake)
  // where the user's normal click action IS the discovery.
  const markHintAsSeen = useCallback(async (hintId) => {
    if (!hintId || seenHints[hintId]) return;
    setSeenHints(prev => {
      if (prev[hintId]) return prev;
      const next = { ...prev, [hintId]: true };
      if (storageKey) localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
    const { bumpTokens: bump, refetchTokens: rTokens, refetchXP: rXP } = bumpRef.current;
    const hint = HINT_MAP[hintId];
    if (hint?.tokens && bump) bump(hint.tokens);
    try {
      await discoverHintApi(hintId);
      if (rTokens) rTokens();
      if (rXP) rXP();
    } catch (err) {
      console.error('Hint discovery API failed:', err);
    }
  }, [seenHints, storageKey]);

  // Activate hints (called by welcome modal dismiss)
  const activateHints = useCallback(() => {
    if (userId) localStorage.setItem(`debono_welcomed_${userId}`, '1');
    setShowWelcome(false);
    setHintsActive(true);
  }, [userId]);

  const dismissWelcome = useCallback(() => {
    if (userId) localStorage.setItem(`debono_welcomed_${userId}`, '1');
    setShowWelcome(false);
    setHintsActive(true);
  }, [userId]);

  // Post-intake queue maintenance — advance the queue head past any
  // items already seen or no longer applicable. We do NOT auto-trigger
  // the discovery modal: the user must click the highlighted DOM
  // element themselves. Click → interceptClick handler in HintLayer →
  // triggerDiscovery → modal → dismiss → seenHints flips → this effect
  // re-runs and shifts the queue head to the next hint, which then
  // becomes the highlighted element on the page.
  useEffect(() => {
    if (showIntake || showWelcome || intakeLoading) return;
    if (postIntakeQueue.length === 0) return;

    const { remainingQueue } = getNextPostIntakeHint(
      postIntakeQueue,
      seenHints,
      intakeExamInterests
    );

    if (remainingQueue.length !== postIntakeQueue.length) {
      setPostIntakeQueue(remainingQueue);
    }
  }, [
    intakeExamInterests,
    intakeLoading,
    postIntakeQueue,
    seenHints,
    showIntake,
    showWelcome,
  ]);

  const markIntakeCompleted = useCallback(async (role, subRole, examInterests, stateScope = null) => {
    const interests = Array.isArray(examInterests) ? examInterests.filter(Boolean) : [];
    const response = await apiClient.post('/api/auth/intake', {
      role,
      sub_role: subRole,
      exam_interests: interests,
      state_scope: stateScope,
    });
    // Persist interests locally so discovery hints can fire even if the
    // backend doesn't echo them back yet. Keyed by user so different
    // accounts on the same browser don't cross-contaminate.
    if (userId) {
      try {
        localStorage.setItem(`debono_intake_interests_${userId}`, JSON.stringify(interests));
      } catch {}
    }
    setIntakeExamInterests(interests);
    try {
      await checkAuth();
    } catch (err) {
      console.error('Auth refresh after intake save failed:', err);
    }
    setShowIntake(false);
    // Intake submission IS the onboarding gate — activate hints so the
    // post-intake queue can drive DOM highlights, and stamp the welcome
    // flag so the legacy Welcome modal does not also fire on top of it.
    if (userId) {
      try { localStorage.setItem(`debono_welcomed_${userId}`, '1'); } catch {}
    }
    // Pre-load the dashboard map with the picked DLC. Dashboard.jsx reads
    // sessionStorage `${interest}_mode === '1'` on mount and auto-enters
    // that DLC's map view — so the user lands directly on (e.g.) the
    // NAPLEX therapeutic-area map instead of an empty dashboard.
    try {
      const firstInterest = interests.find(Boolean);
      ['naplex', 'ptcb', 'mpje'].forEach((dlc) => {
        sessionStorage.setItem(`${dlc}_mode`, dlc === firstInterest ? '1' : '0');
      });
    } catch { /* sessionStorage may be unavailable in private browsing */ }
    setShowWelcome(false);
    setHintsActive(true);
    setPostIntakeQueue(buildPostIntakeHintQueue(interests));
    return response.data;
  }, [checkAuth, userId]);

  const resetHints = useCallback(async () => {
    if (userId) {
      localStorage.removeItem(`debono_hints_seen_${userId}`);
      localStorage.removeItem(`debono_welcomed_${userId}`);
      localStorage.removeItem(`debono_tutorial_progress_${userId}`);
    }
    setSeenHints({});
    setHintsActive(false);
    setActiveDiscoveryId(null);
    setShowWelcome(true);
    setShowIntake(false);
    setIntakeLoading(false);
    setPostIntakeQueue([]);
    try {
      await apiClient.post('/api/hints/reset-onboarding');
    } catch (err) {
      console.error('Hint reset API failed:', err);
    }
  }, [userId]);

  // Computed glow node types for ProgressMap
  const glowNodeTypes = useMemo(() => {
    if (!hintsActive) return null;
    // Map doesn't exist until add_exam is discovered
    if (!seenHints['add_exam']) return null;
    const types = new Set();
    for (const [nodeType, hintId] of Object.entries(NODE_TYPE_TO_HINT)) {
      if (!seenHints[hintId]) {
        // Check gates for the hint
        const hint = HINT_MAP[hintId];
        if (hint?.gatedBy?.length > 0) {
          const gatesMet = hint.gatedBy.every(g => seenHints[g]);
          if (!gatesMet) continue;
        }
        types.add(nodeType);
      }
    }
    return types.size > 0 ? types : null;
  }, [hintsActive, seenHints]);

  const discoveredCount = useMemo(
    () => HINT_IDS.filter(id => seenHints[id]).length,
    [seenHints]
  );

  const onboardingCount = useMemo(
    () => HINTS.filter(hint => hint.onboarding !== false).length,
    []
  );

  const onboardingDiscovered = useMemo(
    () => HINTS.filter(hint => hint.onboarding !== false && seenHints[hint.id]).length,
    [seenHints]
  );

  const allDiscovered = discoveredCount === HINT_IDS.length;

  const allContentDiscovered = useMemo(
    () => CONTENT_HINT_IDS.every(id => seenHints[id]),
    [seenHints]
  );

  const value = useMemo(() => ({
    isHintPending,
    triggerDiscovery,
    confirmDiscovery,
    dismissDiscovery,
    markHintAsSeen,
    activateHints,
    dismissWelcome,
    markIntakeCompleted,
    resetHints,
    activeDiscoveryId,
    glowNodeTypes,
    discoveredCount,
    onboardingCount,
    onboardingDiscovered,
    allDiscovered,
    allContentDiscovered,
    hintsActive: hintsActive && !showIntake,
    seenHints,
    HINT_IDS,
    showWelcome: !showIntake && !intakeLoading && showWelcome,
    showIntake,
    intakeExamInterests,
    postIntakeQueue,
    hasParsedDeck,
    setHasParsedDeck,
  }), [
    isHintPending, triggerDiscovery, confirmDiscovery, dismissDiscovery, markHintAsSeen,
    activateHints, dismissWelcome, markIntakeCompleted, resetHints, activeDiscoveryId, glowNodeTypes,
    discoveredCount, onboardingCount, onboardingDiscovered, allDiscovered,
    allContentDiscovered, hintsActive, intakeLoading, seenHints, showIntake, showWelcome,
    intakeExamInterests, postIntakeQueue, hasParsedDeck,
  ]);

  return <HintCtx.Provider value={value}>{children}</HintCtx.Provider>;
}

export function useHints() {
  const ctx = useContext(HintCtx);
  if (!ctx) throw new Error('useHints must be used within HintProvider');
  return ctx;
}
